import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from "axios";
import { useRoleFlags } from '@/auth/useRoleFlags';
import { normalizeSearchText } from '@/utils/stringUtils';
import DeleteNameConfirm, { deleteNameMatches } from '@/components/DeleteNameConfirm';
import { MapContainer, TileLayer, Marker, Polygon, Polyline, Popup, Tooltip, CircleMarker } from 'react-leaflet';
import { MapLayerTile, MapLayerToggle, useMapLayer } from '@/components/map/MapLayerToggle';
import { MapSearchBox, RecenterMap } from '@/components/map/MapSearchBox';
import {
  NEARBY_SOURCES,
  FitBoundsOnChange,
  NearbyLayerPanel,
  NearbyPointMarkers,
  NearestPlaces,
  buildDirectionsUrl,
  formatDistance,
  openInNewTab,
  pinIcon,
  toCoord,
  useNearbyPoints,
  useNearest,
  usePersistedFlag,
} from '@/components/map/NearbyLayers';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/DatePicker";
import { DateRangePicker } from "@/components/ui/DateRangePicker";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Map as MapIcon, Plus, Edit, Trash2, Search, LayoutGrid, List, TreeDeciduous, Sprout, Wheat, HelpCircle, Calendar, Camera, User, MapPin, Clock, CheckCircle2, XCircle, History, CalendarPlus, ExternalLink, X, Eye, AlertTriangle, Navigation, Copy } from 'lucide-react';
import { toast } from "sonner";
import { useMapEvents } from 'react-leaflet';

function isCanceledError(error) {
  if (!error) return false;
  if (axios.isCancel?.(error)) return true;
  if (error.code === "ERR_CANCELED") return true;
  if (typeof error.message === "string" && error.message.toLowerCase() === "canceled") return true;
  return false;
}
import { ScrollArea } from "@/components/ui/scroll-area";

// Simple location marker for selecting a point
function SimpleLocationMarker({ onLocationSelect }) {
  useMapEvents({
    click(e) {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

// Simple drawing marker for boundary points
function SimpleBoundaryMarker({ onPointAdd }) {
  useMapEvents({
    click(e) {
      onPointAdd(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

// Pasif alan haritasında açılıp kapanan referans katmanları.
const NEARBY_LAYER_KEYS = ['park', 'protokol'];

// Alan Türü Seçenekleri
const AREA_TYPES = [
  { value: 'agaclik', label: 'Ağaçlık', icon: TreeDeciduous, color: 'bg-green-500' },
  { value: 'toprak', label: 'Toprak', icon: Sprout, color: 'bg-amber-600' },
  { value: 'yabani_ot', label: 'Yabani Ot', icon: Wheat, color: 'bg-lime-500' },
  { value: 'diger', label: 'Diğer', icon: HelpCircle, color: 'bg-gray-500' },
];

// Faaliyet Türleri
const ACTIVITY_TYPES = [
  { value: 'temizlik', label: 'Temizlik' },
  { value: 'budama', label: 'Budama' },
  { value: 'ot_bicme', label: 'Ot Biçme' },
  { value: 'copluk_temizleme', label: 'Çöplük Temizleme' },
  { value: 'diger', label: 'Diğer' },
];

// Plan Faaliyet Türleri (Daha spesifik)
const PLAN_ACTIVITY_TYPES = [
  { value: 'form_budama', label: 'Form ve Budama' },
  { value: 'yabani_ot_temizligi', label: 'Yabani Ot Temizliği' },
  { value: 'temizlik', label: 'Genel Temizlik' },
  { value: 'ot_bicme', label: 'Ot Biçme' },
  { value: 'agac_bakimi', label: 'Ağaç Bakımı' },
  { value: 'cit_duzenleme', label: 'Çit Düzenleme' },
  { value: 'diger', label: 'Diğer' },
];

// Faaliyet türü kodundan okunabilir etiket çözer (önce normal, sonra plan türleri)
const resolveActivityLabel = (type) =>
  ACTIVITY_TYPES.find(t => t.value === type)?.label ||
  PLAN_ACTIVITY_TYPES.find(t => t.value === type)?.label ||
  type;

// Plan Durum Renkleri
const PLAN_STATUS_COLORS = {
  planned: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Planlandı' },
  completed: { bg: 'bg-green-100', text: 'text-green-700', label: 'Tamamlandı' },
  incomplete: { bg: 'bg-red-100', text: 'text-red-700', label: 'Tamamlanmadı' },
};

// Mini Map Component
const MiniMap = ({ latitude, longitude, boundaries }) => {
  if (!latitude || !longitude) return null;
  
  return (
    <div className="h-32 w-full rounded-lg overflow-hidden border relative" style={{ zIndex: 0, pointerEvents: 'none' }}>
      <MapContainer
        center={[latitude, longitude]}
        zoom={16}
        style={{ height: '100%', width: '100%', zIndex: 0, pointerEvents: 'none' }}
        zoomControl={false}
        dragging={false}
        scrollWheelZoom={false}
        doubleClickZoom={false}
        keyboard={false}
        touchZoom={false}
        boxZoom={false}
      >
        <TileLayer
          attribution='&copy; OpenStreetMap'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {/* Pin, pasif alan katman rengiyle aynı (turuncu) — harita görünümüyle tutarlı */}
        <Marker position={[latitude, longitude]} icon={pinIcon(NEARBY_SOURCES.pasif.color)} />
        {boundaries && boundaries.length >= 3 && (
          <Polygon 
            positions={boundaries} 
            pathOptions={{ color: '#d97706', fillColor: '#fbbf24', fillOpacity: 0.3 }}
          />
        )}
      </MapContainer>
    </div>
  );
};

// Koordinat kopyalama fonksiyonu - güvenilir fallback ile
const copyLocationToClipboard = (latitude, longitude, address, areaName) => {
  // Google Maps URL - ?q= formatı ile kırmızı iğne (pin) gösterir
  const googleMapsUrl = latitude && longitude 
    ? `https://www.google.com/maps?q=${latitude},${longitude}&z=18`
    : `https://www.google.com/maps/search/${encodeURIComponent(address + ', Ankara')}`;
  
  // Fallback function using textarea
  const fallbackCopy = () => {
    const textArea = document.createElement('textarea');
    textArea.value = googleMapsUrl;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    textArea.style.top = '-9999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        toast.success("Google Maps linki kopyalandı! Tarayıcıda yeni sekme açıp yapıştırın.");
      } else {
        window.prompt('Bu linki kopyalayın ve tarayıcıda açın:', googleMapsUrl);
      }
    } catch (err) {
      window.prompt('Bu linki kopyalayın ve tarayıcıda açın:', googleMapsUrl);
    }
    
    document.body.removeChild(textArea);
  };
  
  // Try modern Clipboard API first, then fallback
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(googleMapsUrl).then(() => {
      toast.success("Google Maps linki kopyalandı! Tarayıcıda yeni sekme açıp yapıştırın.");
    }).catch(() => {
      fallbackCopy();
    });
  } else {
    fallbackCopy();
  }
};

export default function PassiveAreasTab({
  userRole,
  personnelId,
  personnel = [],
  regions = [],
  API: apiProp
}) {
  // Sprint 2 F3: Dinamik yetki + rol flag'leri
  const flagsCtx = useRoleFlags(userRole);
  const { has, isAdmin, isMudur, isMudurYardimcisi, isBolgeMuhendisi, isFormen, isParkGorevlisi, isGolgeAdmin } = flagsCtx;

  const apiUrl = apiProp || API;
  
  const [passiveAreas, setPassiveAreas] = useState([]);
  const [mapLayer, setMapLayer] = useMapLayer("street"); // Harita / Uydu (görünüm haritası)

  // --- Harita yakınlık katmanları (park / protokollü park) ---
  // Taban katman (pasif alanlar). Kapatmak yalnız o anlık bir bakıştır —
  // kalıcı DEĞİL: haritaya her girişte yeniden işaretli gelir.
  const [showPasifLayer, setShowPasifLayer] = useState(true);
  const [showParksLayer, setShowParksLayer] = usePersistedFlag('passiveAreasMap.showParks', false);
  const [showProtokolLayer, setShowProtokolLayer] = usePersistedFlag('passiveAreasMap.showProtokol', false);
  const [linkLine, setLinkLine] = useState(null);             // [[alanLat,alanLng],[parkLat,parkLng]]
  const [linkInfo, setLinkInfo] = useState(null);             // { refName, distance }

  const [formMapLayer, setFormMapLayer] = useMapLayer("street"); // Harita / Uydu (sınır çizim haritası)
  const [formSearchCenter, setFormSearchCenter] = useState(null); // Adres aramada bulunan konum
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRegion, setFilterRegion] = useState("all");
  const [filterAreaType, setFilterAreaType] = useState("all");
  const [viewMode, setViewMode] = useState("cards");
  
  // Dialog states
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isFormReadOnly, setIsFormReadOnly] = useState(false); // Görüntüleme modu için
  const [selectedArea, setSelectedArea] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [areaToDelete, setAreaToDelete] = useState(null);
  // Silme üçüncü onayı — diyalog her açılıp kapandığında sıfırlanır.
  const [areaDeleteConfirmText, setAreaDeleteConfirmText] = useState('');
  
  // Activity dialog states
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [selectedAreaForActivity, setSelectedAreaForActivity] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  
  // Sub-tab state
  const [activeSubTab, setActiveSubTab] = useState("areas");
  
  // Plan states
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState({
    passive_area_id: '',
    activity_type: 'form_budama',
    description: '',
    planned_date: new Date().toISOString().split('T')[0]
  });
  const [deletePlanDialogOpen, setDeletePlanDialogOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [completePlanDialogOpen, setCompletePlanDialogOpen] = useState(false);
  const [planToComplete, setPlanToComplete] = useState(null);
  const [completionStatus, setCompletionStatus] = useState('completed');
  const [completionNote, setCompletionNote] = useState('');
  const [incompleteDialogOpen, setIncompleteDialogOpen] = useState(false);
  
  // History/Inventory states
  const [allActivities, setAllActivities] = useState([]);
  const [loadingAllActivities, setLoadingAllActivities] = useState(false);
  const [historyDateRange, setHistoryDateRange] = useState({
    start: '',
    end: ''
  });
  
  // Formen filtre state'leri (tüm sekmeler için)
  const [filterFormenId, setFilterFormenId] = useState("all");
  const [planFilterFormenId, setPlanFilterFormenId] = useState("all");
  const [planFilterRegion, setPlanFilterRegion] = useState("all");
  const [historyFilterFormenId, setHistoryFilterFormenId] = useState("all");
  const [historyFilterRegion, setHistoryFilterRegion] = useState("all");
  const [historyFilterActivityType, setHistoryFilterActivityType] = useState("all");
  
  // Form state
  const [form, setForm] = useState({
    name: '',
    neighborhood: '',
    address: '',
    latitude: null,
    longitude: null,
    boundaries: null,
    area: null,
    area_type: 'diger',
    region_id: '',
    assigned_formen_id: '',
    assigned_bolge_muhendisi_id: '',
    assigned_mudur_yardimcisi_id: '',
    notes: ''
  });
  
  // Activity form state
  const [activityForm, setActivityForm] = useState({
    activity_type: 'temizlik',
    description: '',
    activity_date: new Date().toISOString().split('T')[0],
    personnel_id: '',
    notes: ''
  });
  
  // Map state
  const [mapCenter, setMapCenter] = useState([39.925533, 32.866287]); // Ankara
  const [drawingMode, setDrawingMode] = useState(false);
  const [drawnPoints, setDrawnPoints] = useState([]);
  
  // Fetch passive areas
  const fetchPassiveAreas = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/api/passive-areas`);
      setPassiveAreas(response.data);
    } catch (error) {
      if (isCanceledError(error)) return;
      console.error("Pasif alanlar yüklenirken hata:", error);
      toast.error("Pasif alanlar yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);
  
  useEffect(() => {
    fetchPassiveAreas();
  }, [fetchPassiveAreas]);

  // Harita görünümü ilk açıldığında park + protokollü park listelerini getirir.
  // "En yakın park" hesabı ve katman overlay'i bu veriden beslenir.
  const { points: nearbyPoints, loading: loadingNearby } = useNearbyPoints(
    apiUrl,
    NEARBY_LAYER_KEYS,
    activeSubTab === 'areas' && viewMode === 'map'
  );

  // Harita her açıldığında pasif alan katmanını geri aç. Kart/tablo görünümüne
  // ya da başka sekmeye gidip dönünce kutucuk boş kalmasın.
  useEffect(() => {
    if (activeSubTab === 'areas' && viewMode === 'map') setShowPasifLayer(true);
  }, [activeSubTab, viewMode]);

  // Fetch activities for an area
  const fetchActivities = async (areaId) => {
    try {
      setLoadingActivities(true);
      const response = await axios.get(`${apiUrl}/api/passive-areas/${areaId}/activities`);
      setActivities(response.data);
    } catch (error) {
      if (isCanceledError(error)) return;
      console.error("Faaliyetler yüklenirken hata:", error);
      toast.error("Faaliyetler yüklenemedi");
    } finally {
      setLoadingActivities(false);
    }
  };
  
  // Fetch plans
  const fetchPlans = useCallback(async () => {
    try {
      setLoadingPlans(true);
      const response = await axios.get(`${apiUrl}/api/passive-area-plans`);
      setPlans(response.data.plans || []);
    } catch (error) {
      if (isCanceledError(error)) return;
      console.error("Planlar yüklenirken hata:", error);
      toast.error("Planlar yüklenemedi");
    } finally {
      setLoadingPlans(false);
    }
  }, [apiUrl]);
  
  // Fetch all activities for history
  const fetchAllActivities = useCallback(async (showValidationError = false) => {
    if (!historyDateRange.start || !historyDateRange.end) {
      setAllActivities([]);
      if (showValidationError) {
        toast.error("Geçmiş verisi için başlangıç ve bitiş tarihi seçin");
      }
      return;
    }
    try {
      setLoadingAllActivities(true);
      const response = await axios.get(`${apiUrl}/api/passive-area-activities`, {
        params: {
          start_date: historyDateRange.start,
          end_date: historyDateRange.end
        }
      });
      setAllActivities(response.data);
    } catch (error) {
      if (isCanceledError(error)) return;
      console.error("Geçmiş yüklenirken hata:", error);
      toast.error("Geçmiş yüklenemedi");
    } finally {
      setLoadingAllActivities(false);
    }
  }, [apiUrl, historyDateRange.end, historyDateRange.start]);
  
  // Fetch based on active tab
  useEffect(() => {
    if (activeSubTab === 'plans') {
      fetchPlans();
    } else if (activeSubTab === 'history') {
      fetchAllActivities();
    }
  }, [activeSubTab, fetchPlans, fetchAllActivities]);
  
  // Plan CRUD operations
  const handleCreatePlan = async () => {
    if (!planForm.passive_area_id) {
      toast.error("Pasif alan seçimi zorunludur");
      return;
    }
    if (!planForm.planned_date) {
      toast.error("Planlanan tarih zorunludur");
      return;
    }
    
    try {
      const payload = {
        ...planForm,
        created_by_id: personnelId
      };
      
      if (editingPlan) {
        await axios.put(`${apiUrl}/api/passive-area-plans/${editingPlan.id}`, payload);
        toast.success("Plan güncellendi");
      } else {
        await axios.post(`${apiUrl}/api/passive-area-plans`, payload);
        toast.success("Plan oluşturuldu");
      }
      
      setPlanDialogOpen(false);
      setEditingPlan(null);
      resetPlanForm();
      fetchPlans();
    } catch (error) {
      if (isCanceledError(error)) return;
      console.error("Plan kaydedilirken hata:", error);
      const errorMsg = error.response?.data?.detail || "Plan kaydedilemedi";
      toast.error(errorMsg);
    }
  };
  
  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setPlanForm({
      passive_area_id: plan.passive_area_id,
      activity_type: plan.activity_type,
      description: plan.description || '',
      planned_date: plan.planned_date
    });
    setPlanDialogOpen(true);
  };
  
  const handleDeletePlan = async () => {
    if (!planToDelete) return;
    
    try {
      await axios.delete(`${apiUrl}/api/passive-area-plans/${planToDelete.id}`);
      toast.success("Plan silindi");
      setDeletePlanDialogOpen(false);
      setPlanToDelete(null);
      fetchPlans();
    } catch (error) {
      if (isCanceledError(error)) return;
      console.error("Plan silinirken hata:", error);
      toast.error("Plan silinemedi");
    }
  };
  
  const handleCompletePlan = async () => {
    if (!planToComplete) return;
    
    try {
      await axios.put(`${apiUrl}/api/passive-area-plans/${planToComplete.id}/complete`, {
        status: completionStatus,
        completion_note: completionNote || null,
        completed_by_id: personnelId
      });
      toast.success(completionStatus === 'completed' ? "Plan tamamlandı" : "Plan tamamlanmadı olarak işaretlendi");
      setCompletePlanDialogOpen(false);
      setPlanToComplete(null);
      setCompletionStatus('completed');
      setCompletionNote('');
      fetchPlans();
    } catch (error) {
      if (isCanceledError(error)) return;
      console.error("Plan durumu güncellenirken hata:", error);
      toast.error("Plan durumu güncellenemedi");
    }
  };
  
  // Quick complete - for Tamamlandı button (no dialog needed)
  const handleQuickComplete = async (plan, status) => {
    try {
      await axios.put(`${apiUrl}/api/passive-area-plans/${plan.id}/complete`, {
        status: status,
        completion_note: null,
        completed_by_id: personnelId
      });
      toast.success("Plan tamamlandı");
      fetchPlans();
    } catch (error) {
      if (isCanceledError(error)) return;
      console.error("Plan tamamlanırken hata:", error);
      toast.error("Plan tamamlanamadı");
    }
  };
  
  // Handle incomplete with reason
  const handleIncompletePlan = async () => {
    if (!planToComplete) return;
    if (!completionNote.trim()) {
      toast.error("Lütfen tamamlanmama nedenini yazın");
      return;
    }
    
    try {
      await axios.put(`${apiUrl}/api/passive-area-plans/${planToComplete.id}/complete`, {
        status: 'incomplete',
        completion_note: completionNote,
        completed_by_id: personnelId
      });
      toast.success("Plan tamamlanmadı olarak işaretlendi");
      setIncompleteDialogOpen(false);
      setPlanToComplete(null);
      setCompletionNote('');
      fetchPlans();
    } catch (error) {
      if (isCanceledError(error)) return;
      console.error("Plan durumu güncellenirken hata:", error);
      toast.error("Plan durumu güncellenemedi");
    }
  };
  
  const resetPlanForm = () => {
    setPlanForm({
      passive_area_id: '',
      activity_type: 'form_budama',
      description: '',
      planned_date: new Date().toISOString().split('T')[0]
    });
  };
  
  const openNewPlanDialog = () => {
    resetPlanForm();
    setEditingPlan(null);
    setPlanDialogOpen(true);
  };
  
  // Permission-based gates (admin paneldeki Rol Yönetimi'nden anında değişir)
  const canCreate = has('pasif_alan.olustur');
  const canEdit = has('pasif_alan.duzenle');
  const canDelete = has('pasif_alan.sil');
  const canSeeAllScope = has('pasif_alan.tum_kapsam');
  // Plan oluşturma da "pasif_alan.olustur" yetkisine bağlı
  const canCreatePlan = canCreate;
  
  // Filter personnel by role
  const formens = personnel.filter(p => p.role === 'formen');
  const bolgeMuhendisleri = personnel.filter(p => p.role === 'bolge_muhendisi');
  const mudurYardimcilari = personnel.filter(p => p.role === 'mudur_yardimcisi');
  
  // Kullanıcının erişebildiği bölge ID'lerini bul
  const myRegionIds = useMemo(() => {
    if (isAdmin || isMudur) {
      return regions.map(r => r.id); // Admin/Mudur tum bolgeleri gorur
    }
    if (isMudurYardimcisi) {
      // Kendisine atanmış bölgeleri bul
      return regions
        .filter(r => r.mudur_yardimcisi_id === personnelId)
        .map(r => r.id);
    }
    if (isBolgeMuhendisi) {
      // Kendisine atanmış bölgeleri bul
      return regions
        .filter(r => r.bolge_muhendisi_id === personnelId)
        .map(r => r.id);
    }
    if (isFormen) {
      // Formen: (1) Kendisine atanmış pasif alanların bölgeleri (2) Kendisinin atandığı bölgeler (assigned_formen_ids)
      const formenAreaRegions = passiveAreas
        .filter(a => a.assigned_formen_id === personnelId)
        .map(a => a.region_id)
        .filter(Boolean);
      const regionsWhereFormenAssigned = (regions || [])
        .filter(r => (r.assigned_formen_ids || []).includes(personnelId))
        .map(r => r.id);
      return [...new Set([...formenAreaRegions, ...regionsWhereFormenAssigned])];
    }
    return [];
  }, [userRole, personnelId, regions, passiveAreas]);
  
  // Kullanıcının görebileceği bölgeler (filtre dropdown için)
  const visibleRegions = useMemo(() => {
    if (isAdmin || isMudur) {
      return regions;
    }
    if (isMudurYardimcisi) {
      // Müdür Yardımcısı: Kendisine atanmış pasif alanların bölgelerini görür
      const myAreaRegionIds = passiveAreas
        .filter(a => a.assigned_mudur_yardimcisi_id === personnelId)
        .map(a => a.region_id)
        .filter(Boolean);
      const uniqueRegionIds = [...new Set([...myRegionIds, ...myAreaRegionIds])];
      return regions.filter(r => uniqueRegionIds.includes(r.id));
    }
    if (isBolgeMuhendisi) {
      // Bölge Mühendisi: Kendisine atanmış pasif alanların bölgelerini görür
      const myAreaRegionIds = passiveAreas
        .filter(a => a.assigned_bolge_muhendisi_id === personnelId)
        .map(a => a.region_id)
        .filter(Boolean);
      const uniqueRegionIds = [...new Set([...myRegionIds, ...myAreaRegionIds])];
      return regions.filter(r => uniqueRegionIds.includes(r.id));
    }
    // Formen: Kendisine atanmış pasif alanların bölgelerini görür
    return regions.filter(r => myRegionIds.includes(r.id));
  }, [userRole, regions, myRegionIds, passiveAreas, personnelId]);
  
  // Kullanıcının erişebildiği pasif alanlar (rol bazlı)
  const accessibleAreas = useMemo(() => {
    if (isAdmin || isMudur) {
      return passiveAreas;
    }
    if (isMudurYardimcisi) {
      return passiveAreas.filter(a => 
        myRegionIds.includes(a.region_id) || a.assigned_mudur_yardimcisi_id === personnelId
      );
    }
    if (isBolgeMuhendisi) {
      return passiveAreas.filter(a => 
        myRegionIds.includes(a.region_id) || a.assigned_bolge_muhendisi_id === personnelId
      );
    }
    if (isFormen) {
      // Formen: Doğrudan atanmış alanlar + atandığı bölgelerdeki (assigned_formen_ids) tüm pasif alanlar
      return passiveAreas.filter(a =>
        a.assigned_formen_id === personnelId || (a.region_id && myRegionIds.includes(a.region_id))
      );
    }
    return passiveAreas;
  }, [userRole, personnelId, passiveAreas, myRegionIds]);
  
  // Kullanıcının görebileceği formenler (filtre dropdown için)
  const visibleFormens = useMemo(() => {
    // Erişilebilir pasif alanlara atanmış formenler
    const formensInMyAreas = [...new Map(
      accessibleAreas
        .filter(a => a.assigned_formen_id && a.assigned_formen_name)
        .map(a => [a.assigned_formen_id, { id: a.assigned_formen_id, name: a.assigned_formen_name }])
    ).values()].sort((a, b) => a.name?.localeCompare(b.name));
    
    return formensInMyAreas;
  }, [accessibleAreas]);
  
  // Pasif alanlara atanmış formenler listesi (eski - geriye uyumluluk için)
  const assignedFormens = visibleFormens;

  // Geçmiş/Envanter faaliyet türü filtresi için dinamik seçenekler.
  // Statik listeye bağlı kalmadan, getirilen kayıtlardaki türlerden üretilir.
  const visibleActivityTypes = useMemo(() => {
    return [...new Map(
      allActivities
        .filter(a => a.activity_type)
        .map(a => [a.activity_type, { value: a.activity_type, label: resolveActivityLabel(a.activity_type) }])
    ).values()].sort((a, b) => a.label?.localeCompare(b.label, 'tr'));
  }, [allActivities]);

  // Seçili faaliyet türü yeni sonuçlarda yoksa filtreyi sıfırla (tutarlılık)
  useEffect(() => {
    if (historyFilterActivityType !== 'all' &&
        !visibleActivityTypes.some(t => t.value === historyFilterActivityType)) {
      setHistoryFilterActivityType('all');
    }
  }, [visibleActivityTypes, historyFilterActivityType]);
  
  // Filter passive areas (accessibleAreas zaten rol bazlı filtrelenmiş)
  const filteredAreas = accessibleAreas.filter(area => {
    const matchesSearch = normalizeSearchText(area.name).includes(normalizeSearchText(searchTerm)) ||
                         normalizeSearchText(area.neighborhood).includes(normalizeSearchText(searchTerm)) ||
                         normalizeSearchText(area.address).includes(normalizeSearchText(searchTerm));
    const matchesRegion = filterRegion === 'all' || area.region_id === filterRegion;
    const matchesType = filterAreaType === 'all' || area.area_type === filterAreaType;
    const matchesFormen = filterFormenId === 'all' || area.assigned_formen_id === filterFormenId;
    
    return matchesSearch && matchesRegion && matchesType && matchesFormen;
  }).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr')); // Alfabetik sıralama

  // --- Harita: koordinatlı alanlar + yakınlık referansları ------------------

  // Haritada gösterilebilir (koordinatlı) pasif alanlar.
  const mapAreas = filteredAreas
    .map((area) => ({ ...area, _lat: toCoord(area.latitude), _lng: toCoord(area.longitude) }))
    .filter((area) => area._lat !== null && area._lng !== null);

  // Memo bağımlılığı için stabil imza (filteredAreas her render'da yeniden üretiliyor).
  const mapAreasKey = mapAreas.map((a) => `${a.id}:${a._lat},${a._lng}`).join('|');

  const parkPoints = useMemo(() => nearbyPoints.park || [], [nearbyPoints.park]);
  const protokolPoints = useMemo(() => nearbyPoints.protokol || [], [nearbyPoints.protokol]);

  // Park + protokollü park referansları tek listede (mesafe hesabı için).
  const referencePoints = useMemo(
    () => [...parkPoints, ...protokolPoints],
    [parkPoints, protokolPoints]
  );

  // Katman seçimine göre haritada çizilecek referanslar.
  const visibleReferences = useMemo(() => {
    const rows = [];
    if (showParksLayer) rows.push(...parkPoints);
    if (showProtokolLayer) rows.push(...protokolPoints);
    return rows;
  }, [showParksLayer, showProtokolLayer, parkPoints, protokolPoints]);

  // Her pasif alan için en yakın 3 park/protokollü park (kuş uçuşu).
  const nearestOrigins = useMemo(
    () => mapAreas.map((a) => ({ id: a.id, kind: 'pasif', lat: a._lat, lng: a._lng })),
    // mapAreas her render'da yeniden üretiliyor; tetikleyici stabil imza.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mapAreasKey]
  );
  const nearestByAreaId = useNearest(nearestOrigins, referencePoints);

  // Otomatik çerçeveleme: bağlantı çizgisi varsa iki noktaya, yoksa tüm alanlara.
  const mapFitBounds = linkLine || mapAreas.map((a) => [a._lat, a._lng]);
  const mapFitKey = linkLine ? `link:${JSON.stringify(linkLine)}` : `areas:${mapAreasKey}`;

  // "En yakın" listesinden bir referansa tıklanınca: katmanı aç, çizgiyi kur.
  const focusReference = (area, ref) => {
    if (ref.kind === 'park') setShowParksLayer(true);
    else setShowProtokolLayer(true);
    setLinkLine([[area._lat, area._lng], [ref.lat, ref.lng]]);
    setLinkInfo({ areaName: area.name, refName: ref.name, distance: ref.distance });
  };

  const clearLink = () => {
    setLinkLine(null);
    setLinkInfo(null);
  };

  // Open dialog for new area
  const handleAddNew = () => {
    setForm({
      name: '',
      neighborhood: '',
      address: '',
      latitude: null,
      longitude: null,
      boundaries: null,
      area: null,
      area_type: 'diger',
      region_id: '',
      assigned_formen_id: '',
      assigned_bolge_muhendisi_id: '',
      assigned_mudur_yardimcisi_id: '',
      notes: ''
    });
    setDrawnPoints([]);
    setIsEditing(false);
    setSelectedArea(null);
    setIsDialogOpen(true);
  };
  
  // Open dialog for editing
  const handleEdit = (area) => {
    setForm({
      name: area.name || '',
      neighborhood: area.neighborhood || '',
      address: area.address || '',
      latitude: area.latitude,
      longitude: area.longitude,
      boundaries: area.boundaries,
      area: area.area,
      area_type: area.area_type || 'diger',
      region_id: area.region_id || '',
      assigned_formen_id: area.assigned_formen_id || '',
      assigned_bolge_muhendisi_id: area.assigned_bolge_muhendisi_id || '',
      assigned_mudur_yardimcisi_id: area.assigned_mudur_yardimcisi_id || '',
      notes: area.notes || ''
    });
    setDrawnPoints(area.boundaries || []);
    if (area.latitude && area.longitude) {
      setMapCenter([area.latitude, area.longitude]);
    }
    setIsEditing(true);
    setIsFormReadOnly(false);
    setSelectedArea(area);
    setIsDialogOpen(true);
  };
  
  // Görüntüleme modu - Bölge Mühendisi ve Formen için
  const handleView = (area) => {
    setForm({
      name: area.name || '',
      neighborhood: area.neighborhood || '',
      address: area.address || '',
      latitude: area.latitude,
      longitude: area.longitude,
      boundaries: area.boundaries,
      area: area.area,
      area_type: area.area_type || 'diger',
      region_id: area.region_id || '',
      assigned_formen_id: area.assigned_formen_id || '',
      assigned_bolge_muhendisi_id: area.assigned_bolge_muhendisi_id || '',
      assigned_mudur_yardimcisi_id: area.assigned_mudur_yardimcisi_id || '',
      notes: area.notes || ''
    });
    setDrawnPoints(area.boundaries || []);
    if (area.latitude && area.longitude) {
      setMapCenter([area.latitude, area.longitude]);
    }
    setIsEditing(true);
    setIsFormReadOnly(true); // Salt okunur mod
    setSelectedArea(area);
    setIsDialogOpen(true);
  };
  
  // Handle location select from map
  const handleLocationSelect = (lat, lng) => {
    setForm(prev => ({ ...prev, latitude: lat, longitude: lng }));
    setMapCenter([lat, lng]);
  };
  
  // Handle boundary drawing
  const handleBoundaryPointAdd = (lat, lng) => {
    if (drawingMode) {
      setDrawnPoints(prev => [...prev, [lat, lng]]);
    }
  };
  
  // Calculate polygon area
  const calculatePolygonArea = (points) => {
    if (points.length < 3) return 0;
    let area = 0;
    const n = points.length;
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      area += points[i][1] * points[j][0];
      area -= points[j][1] * points[i][0];
    }
    area = Math.abs(area) / 2;
    // Convert to square meters (approximate)
    return Math.round(area * 111319.9 * 111319.9 * Math.cos(points[0][0] * Math.PI / 180));
  };
  
  // Save boundaries
  const handleSaveBoundaries = () => {
    if (drawnPoints.length >= 3) {
      const calculatedArea = calculatePolygonArea(drawnPoints);
      setForm(prev => ({ 
        ...prev, 
        boundaries: drawnPoints,
        area: calculatedArea
      }));
      setDrawingMode(false);
      toast.success("Sınırlar kaydedildi");
    } else {
      toast.error("En az 3 nokta gerekli");
    }
  };
  
  // Clear boundaries
  const handleClearBoundaries = () => {
    setDrawnPoints([]);
    setForm(prev => ({ ...prev, boundaries: null, area: null }));
  };
  
  // Submit form
  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error("Pasif alan adı zorunludur");
      return;
    }
    
    // Scroll pozisyonunu kaydet - main content container için
    const mainContent = document.querySelector('.flex-1.overflow-auto');
    const savedScrollTop = mainContent ? mainContent.scrollTop : 0;
    
    try {
      const payload = {
        ...form,
        boundaries: drawnPoints.length >= 3 ? drawnPoints : form.boundaries
      };
      
      if (isEditing && selectedArea) {
        await axios.put(`${apiUrl}/api/passive-areas/${selectedArea.id}`, payload);
        toast.success("Pasif alan güncellendi");
      } else {
        await axios.post(`${apiUrl}/api/passive-areas`, payload);
        toast.success("Pasif alan oluşturuldu");
      }
      
      setIsDialogOpen(false);
      fetchPassiveAreas();
      
      // Scroll pozisyonunu restore et
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (mainContent) mainContent.scrollTop = savedScrollTop;
        }, 100);
      });
    } catch (error) {
      if (isCanceledError(error)) return;
      console.error("Pasif alan kaydedilirken hata:", error);
      toast.error("Kaydetme sırasında hata oluştu");
    }
  };
  
  useEffect(() => {
    setAreaDeleteConfirmText('');
  }, [areaToDelete, deleteDialogOpen]);

  const areaDeleteConfirmed =
    !!areaToDelete && deleteNameMatches(areaDeleteConfirmText, areaToDelete.name);

  // Delete area
  const handleDelete = async () => {
    // Üçüncü onay: alan adı birebir yazılmadan istek gitmez.
    if (!areaToDelete || !areaDeleteConfirmed) return;

    try {
      await axios.delete(`${apiUrl}/api/passive-areas/${areaToDelete.id}`);
      toast.success("Pasif alan silindi");
      setDeleteDialogOpen(false);
      setAreaToDelete(null);
      fetchPassiveAreas();
    } catch (error) {
      if (isCanceledError(error)) return;
      console.error("Pasif alan silinirken hata:", error);
      toast.error("Silme sırasında hata oluştu");
    }
  };
  
  // Open activity dialog
  const handleOpenActivities = (area) => {
    setSelectedAreaForActivity(area);
    setActivityForm({
      activity_type: 'temizlik',
      description: '',
      activity_date: new Date().toISOString().split('T')[0],
      personnel_id: '',
      notes: ''
    });
    fetchActivities(area.id);
    setActivityDialogOpen(true);
  };
  
  // Submit activity
  const handleSubmitActivity = async () => {
    if (!activityForm.description.trim()) {
      toast.error("Faaliyet açıklaması zorunludur");
      return;
    }
    
    try {
      await axios.post(`${apiUrl}/api/passive-area-activities`, {
        passive_area_id: selectedAreaForActivity.id,
        ...activityForm
      });
      toast.success("Faaliyet kaydedildi");
      fetchActivities(selectedAreaForActivity.id);
      setActivityForm({
        activity_type: 'temizlik',
        description: '',
        activity_date: new Date().toISOString().split('T')[0],
        personnel_id: '',
        notes: ''
      });
    } catch (error) {
      if (isCanceledError(error)) return;
      console.error("Faaliyet kaydedilirken hata:", error);
      toast.error("Faaliyet kaydetme hatası");
    }
  };
  
  // Delete activity
  const handleDeleteActivity = async (activityId) => {
    try {
      await axios.delete(`${apiUrl}/api/passive-area-activities/${activityId}`);
      toast.success("Faaliyet silindi");
      fetchActivities(selectedAreaForActivity.id);
    } catch (error) {
      if (isCanceledError(error)) return;
      console.error("Faaliyet silinirken hata:", error);
      toast.error("Faaliyet silme hatası");
    }
  };
  
  // Get area type info
  const getAreaTypeInfo = (type) => {
    return AREA_TYPES.find(t => t.value === type) || AREA_TYPES[3];
  };
  
  // Get region name
  const getRegionName = (regionId) => {
    const region = regions.find(r => r.id === regionId);
    return region?.name || '-';
  };

  return (
    <TabsContent value="passive-areas" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <MapIcon className="h-8 w-8 text-amber-600" />
          <div>
            <h2 className="text-2xl font-bold">Pasif Alanlar</h2>
            <p className="text-gray-500">Yapılmamış parklar, yeşil alanlar ve boş araziler</p>
          </div>
        </div>
        <Badge variant="outline" className="text-lg px-4 py-2">
          {filteredAreas.length} / {passiveAreas.length} Alan
        </Badge>
      </div>
      
      {/* Sub-tabs Navigation */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 max-w-xl">
          <TabsTrigger value="areas" className="flex items-center gap-2">
            <MapIcon className="h-4 w-4" />
            Pasif Alanlar
          </TabsTrigger>
          <TabsTrigger value="plans" className="flex items-center gap-2">
            <CalendarPlus className="h-4 w-4" />
            Planlama
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            Geçmiş / Envanter
          </TabsTrigger>
        </TabsList>
      </Tabs>
      
      {/* ===== AREAS TAB ===== */}
      {activeSubTab === 'areas' && (
        <>
          {/* Filters & Actions */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap items-center gap-4">
                {/* Search */}
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Alan ara (ad, mahalle, adres)..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                {/* Region Filter - Sadece erişilebilir bölgeler */}
                {visibleRegions.length > 0 && (
                  <SearchableSelect
                    value={filterRegion}
                    onChange={setFilterRegion}
                    allLabel="Tüm Bölgeler"
                    searchPlaceholder="Bölge ara..."
                    className="w-[180px]"
                    options={visibleRegions.map((region) => ({ id: region.id, name: region.name }))}
                  />
                )}

                {/* Area Type Filter */}
                <SearchableSelect
                  value={filterAreaType}
                  onChange={setFilterAreaType}
                  allLabel="Tüm Türler"
                  searchPlaceholder="Alan türü ara..."
                  className="w-[150px]"
                  options={AREA_TYPES.map((type) => ({ id: type.value, name: type.label }))}
                />

                {/* Formen Filter - Geniş kapsamlı kullanıcılar (yönetici) için */}
                {canSeeAllScope && visibleFormens.length > 0 && (
                  <SearchableSelect
                    value={filterFormenId}
                    onChange={setFilterFormenId}
                    allLabel="Tüm Formenler"
                    searchPlaceholder="Formen ara..."
                    className="w-[180px]"
                    options={visibleFormens.map((formen) => ({ id: formen.id, name: formen.name }))}
                  />
                )}
                
                {/* Görünüm modu — üstüne gelince (hover) etiket yazısı çıkar */}
                <div className="flex border rounded-lg">
                  <Button
                    variant={viewMode === 'cards' ? 'default' : 'ghost'}
                    size="sm"
                    title="Kart"
                    onClick={() => setViewMode('cards')}
                    className="group gap-0 hover:gap-1.5"
                  >
                    <LayoutGrid className="h-4 w-4" />
                    <span className="max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-all duration-200 group-hover:max-w-[60px] group-hover:opacity-100">Kart</span>
                  </Button>
                  <Button
                    variant={viewMode === 'table' ? 'default' : 'ghost'}
                    size="sm"
                    title="Tablo"
                    onClick={() => setViewMode('table')}
                    className="group gap-0 hover:gap-1.5"
                  >
                    <List className="h-4 w-4" />
                    <span className="max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-all duration-200 group-hover:max-w-[60px] group-hover:opacity-100">Tablo</span>
                  </Button>
                  <Button
                    variant={viewMode === 'map' ? 'default' : 'ghost'}
                    size="sm"
                    title="Harita"
                    onClick={() => setViewMode('map')}
                    className="group gap-0 hover:gap-1.5"
                  >
                    <MapIcon className="h-4 w-4" />
                    <span className="max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-all duration-200 group-hover:max-w-[60px] group-hover:opacity-100">Harita</span>
                  </Button>
                </div>
                
                {/* Add Button */}
                {canCreate && (
                  <Button onClick={handleAddNew} className="bg-amber-600 hover:bg-amber-700">
                    <Plus className="h-4 w-4 mr-2" />
                    Yeni Pasif Alan
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* Content */}
          {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="h-20 bg-gray-200" />
              <CardContent className="space-y-3 pt-4">
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredAreas.length === 0 ? (
        <Card className="p-12 text-center">
          <MapIcon className="h-16 w-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-600">Pasif alan bulunamadı</h3>
          <p className="text-gray-500 mt-2">Yeni bir pasif alan eklemek için yukarıdaki butonu kullanın.</p>
        </Card>
      ) : viewMode === 'cards' ? (
        /* Card View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAreas.map(area => {
            const typeInfo = getAreaTypeInfo(area.area_type);
            const TypeIcon = typeInfo.icon;
            
            return (
              <Card
                key={area.id}
                className={`hover:shadow-lg transition-shadow ${(!canEdit && has('pasif_alan.gor')) ? 'cursor-pointer' : ''}`}
                onClick={() => {
                  // Düzenleme yetkisi yoksa karta tıklayınca görüntüleme modunda aç
                  if (!canEdit && has('pasif_alan.gor')) {
                    handleView(area);
                  }
                }}
              >
                <CardHeader className={`${typeInfo.color} text-white rounded-t-lg`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TypeIcon className="h-5 w-5" />
                      <CardTitle className="text-lg">{area.name}</CardTitle>
                    </div>
                    <Badge variant="secondary" className="bg-white/20 text-white">
                      {typeInfo.label}
                    </Badge>
                  </div>
                  {area.neighborhood && (
                    <CardDescription className="text-white/80">{area.neighborhood}</CardDescription>
                  )}
                </CardHeader>
                <CardContent className="pt-4 space-y-3">
                  {/* Mini Map Preview - Click to copy location */}
                  {(area.latitude && area.longitude) && (
                    <div className="relative">
                      <MiniMap 
                        latitude={area.latitude} 
                        longitude={area.longitude} 
                        boundaries={area.boundaries}
                      />
                      {/* Clickable overlay for copy functionality */}
                      <div 
                        className="absolute inset-0 cursor-pointer hover:bg-black/5 transition-colors rounded-lg flex items-center justify-center group"
                        style={{ zIndex: 10 }}
                        title="Tıkla - Google Maps linkini kopyala"
                        onClick={() => copyLocationToClipboard(area.latitude, area.longitude, area.address, area.name)}
                      >
                        <span className="bg-white/90 px-2 py-1 rounded text-xs text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity shadow">
                          📋 Konumu Kopyala
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {/* Clickable Address - Copy to clipboard */}
                  {area.address && (
                    <div 
                      className="text-sm text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-2 group cursor-pointer"
                      onClick={() => copyLocationToClipboard(area.latitude, area.longitude, area.address, area.name)}
                      title="Tıkla - Google Maps linkini kopyala"
                    >
                      <MapPin className="h-4 w-4" />
                      <span className="flex-1">{area.address}</span>
                      <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  )}
                  
                  {area.area && (
                    <p className="text-sm font-medium">
                      Toplam Alan: <span className="text-amber-600">{area.area.toLocaleString()} m²</span>
                    </p>
                  )}
                  
                  <div className="text-sm text-gray-600">
                    <p>Bölge: <span className="font-medium">{getRegionName(area.region_id)}</span></p>
                  </div>
                  
                  {/* Assigned Personnel */}
                  <div className="space-y-1 text-xs">
                    {area.assigned_formen_name && (
                      <Badge variant="outline" className="mr-1">
                        <User className="h-3 w-3 mr-1" />
                        Formen: {area.assigned_formen_name}
                      </Badge>
                    )}
                    {area.assigned_bolge_muhendisi_name && (
                      <Badge variant="outline" className="mr-1">
                        B.Müh: {area.assigned_bolge_muhendisi_name}
                      </Badge>
                    )}
                  </div>
                  
                  {/* Actions: düzenleme/silme yetkisi olanlar */}
                  {(canEdit || canDelete) && (
                    <div className="flex gap-2 pt-2 border-t justify-end" onClick={(e) => e.stopPropagation()}>
                      {canEdit && (
                        <Button variant="outline" size="sm" onClick={() => handleEdit(area)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => { setAreaToDelete(area); setDeleteDialogOpen(true); }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  )}
                  {/* Düzenleyemeyen ama görüntüleyebilen kullanıcılar için "Görüntüle" */}
                  {!canEdit && has('pasif_alan.gor') && (
                    <div className="flex gap-2 pt-2 border-t justify-end" onClick={(e) => e.stopPropagation()}>
                      <Button variant="outline" size="sm" onClick={() => handleView(area)} title="Detayları Görüntüle">
                        <Eye className="h-4 w-4 mr-1" />
                        Görüntüle
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : viewMode === 'table' ? (
        /* Table View */
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Alan Adı</TableHead>
                <TableHead>Mahalle</TableHead>
                <TableHead>Tür</TableHead>
                <TableHead>Alan (m²)</TableHead>
                <TableHead>Bölge</TableHead>
                <TableHead>Formen</TableHead>
                <TableHead className="text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAreas.map(area => {
                const typeInfo = getAreaTypeInfo(area.area_type);
                return (
                  <TableRow key={area.id}>
                    <TableCell className="font-medium">{area.name}</TableCell>
                    <TableCell>{area.neighborhood || '-'}</TableCell>
                    <TableCell>
                      <Badge className={`${typeInfo.color} text-white`}>{typeInfo.label}</Badge>
                    </TableCell>
                    <TableCell>{area.area?.toLocaleString() || '-'}</TableCell>
                    <TableCell>{getRegionName(area.region_id)}</TableCell>
                    <TableCell>{area.assigned_formen_name || '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleOpenActivities(area)}>
                          <Calendar className="h-4 w-4" />
                        </Button>
                        {/* Düzenleyemeyenler için "Görüntüle" */}
                        {!canEdit && has('pasif_alan.gor') && (
                          <Button variant="ghost" size="sm" onClick={() => handleView(area)} title="Detayları Görüntüle">
                            <Eye className="h-4 w-4" />
                          </Button>
                        )}
                        {canEdit && (
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(area)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setAreaToDelete(area); setDeleteDialogOpen(true); }}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      ) : (
        /* Map View */
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MapIcon className="h-5 w-5 text-amber-600" />
                <span>Pasif Alan Haritası</span>
              </div>
              <Badge variant="outline">
                {mapAreas.length} / {filteredAreas.length} koordinatlı alan
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {mapAreas.length > 0 ? (
              <div className="h-[600px] rounded-lg overflow-hidden border relative">
                <MapLayerToggle value={mapLayer} onChange={setMapLayer} floating />

                {/* Katman paneli — pasif alanın çevresindeki parkları görünür kılar */}
                <NearbyLayerPanel
                  base={{
                    ...NEARBY_SOURCES.pasif,
                    count: mapAreas.length,
                    checked: showPasifLayer,
                    onChange: setShowPasifLayer,
                  }}
                  layers={[
                    {
                      ...NEARBY_SOURCES.park,
                      count: parkPoints.length,
                      checked: showParksLayer,
                      onChange: setShowParksLayer,
                    },
                    {
                      ...NEARBY_SOURCES.protokol,
                      count: protokolPoints.length,
                      checked: showProtokolLayer,
                      onChange: setShowProtokolLayer,
                    },
                  ]}
                  loading={loadingNearby}
                  showClear={Boolean(linkLine)}
                  onClearLink={clearLink}
                />

                <MapContainer
                  center={[39.925533, 32.866287]}
                  zoom={12}
                  style={{ height: '100%', width: '100%' }}
                >
                  <MapLayerTile layer={mapLayer} />
                  {/* Filtre değişince görünür noktalara otomatik oturt */}
                  <FitBoundsOnChange bounds={mapFitBounds} boundsKey={mapFitKey} />
                  {/* Çizilen pasif alan sınırları (polygon) — hem harita hem uydu görünümünde */}
                  {showPasifLayer && filteredAreas
                    .filter((area) => Array.isArray(area.boundaries) && area.boundaries.length >= 3)
                    .map((area) => (
                      <Polygon
                        key={`area-boundary-${area.id}`}
                        positions={area.boundaries}
                        pathOptions={{ color: '#d97706', fillColor: '#fbbf24', fillOpacity: 0.3, weight: 2 }}
                      >
                        <Tooltip sticky direction="top">{area.name}</Tooltip>
                      </Polygon>
                    ))}
                  {/* Park / protokollü park katmanları — küçük daireler, pasif alan
                      pin'lerinin görsel baskınlığını bozmadan çevreyi gösterir */}
                  <NearbyPointMarkers points={visibleReferences} regionNameOf={getRegionName} />

                  {/* Pasif alan ↔ seçilen park bağlantı çizgisi */}
                  {linkLine && (
                    <Polyline
                      positions={linkLine}
                      pathOptions={{ color: '#b45309', weight: 2.5, dashArray: '6 6' }}
                    >
                      <Tooltip sticky direction="top">
                        {linkInfo?.refName} — {formatDistance(linkInfo?.distance)}
                      </Tooltip>
                    </Polyline>
                  )}

                  {showPasifLayer && filteredAreas
                    .filter((area) => area.latitude && area.longitude)
                    .map((area) => {
                      const typeInfo = getAreaTypeInfo(area.area_type);
                      const nearest = nearestByAreaId[area.id] || [];
                      const areaPoint = mapAreas.find((a) => a.id === area.id);
                      return (
                        <Marker
                          key={area.id}
                          position={[area.latitude, area.longitude]}
                          icon={pinIcon(NEARBY_SOURCES.pasif.color)}
                        >
                          <Tooltip direction="top" offset={[0, -12]}>
                            <span className="font-medium">{area.name}</span>
                          </Tooltip>
                          <Popup>
                            <div className="min-w-[250px] max-w-[320px] space-y-2">
                              <h3 className="font-bold text-base text-amber-700 border-b pb-1">{area.name}</h3>
                              <p className="text-sm">
                                <span className="font-medium text-gray-600">Tür:</span> {typeInfo.label}
                              </p>
                              {area.neighborhood && (
                                <p className="text-sm">
                                  <span className="font-medium text-gray-600">Mahalle:</span> {area.neighborhood}
                                </p>
                              )}
                              {area.address && (
                                <p className="text-sm">
                                  <span className="font-medium text-gray-600">Adres:</span>{' '}
                                  <a
                                    href={buildDirectionsUrl(area.latitude, area.longitude, area.address)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-amber-700 underline-offset-2 hover:underline"
                                    title="Yol tarifi al ve git"
                                  >
                                    {area.address}
                                  </a>
                                </p>
                              )}
                              <p className="text-sm">
                                <span className="font-medium text-gray-600">Bölge:</span> {getRegionName(area.region_id)}
                              </p>
                              {area.area && (
                                <p className="text-sm">
                                  <span className="font-medium text-gray-600">Alan:</span> {area.area.toLocaleString('tr-TR')} m²
                                </p>
                              )}
                              {area.assigned_formen_name && (
                                <p className="text-sm">
                                  <span className="font-medium text-gray-600">Formen:</span> {area.assigned_formen_name}
                                </p>
                              )}
                              {/* En yakın park / protokollü park — "burası neyin yanında?" */}
                              <NearestPlaces
                                nearest={nearest}
                                loading={loadingNearby}
                                title="En yakın park"
                                onSelect={(ref) => areaPoint && focusReference(areaPoint, ref)}
                              />

                              <div className="flex gap-1 border-t pt-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="flex-1"
                                  onClick={() => handleView(area)}
                                >
                                  <Eye className="mr-1 h-3 w-3" />
                                  Detaylar
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="flex-1"
                                  onClick={() => openInNewTab(buildDirectionsUrl(area.latitude, area.longitude, area.address))}
                                >
                                  <Navigation className="mr-1 h-3 w-3" />
                                  Yol Tarifi
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  title="Konum linkini kopyala"
                                  onClick={() => copyLocationToClipboard(area.latitude, area.longitude, area.address, area.name)}
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          </Popup>
                        </Marker>
                      );
                    })}
                </MapContainer>
              </div>
            ) : (
              <div className="h-[400px] flex flex-col items-center justify-center bg-gray-50 rounded-lg border-2 border-dashed">
                <MapIcon className="h-16 w-16 text-gray-300 mb-4" />
                <p className="text-gray-500 text-lg font-medium">Koordinatı Olan Pasif Alan Bulunamadı</p>
                <p className="text-gray-400 text-sm mt-1">Filtreleri temizleyip tekrar deneyebilirsiniz</p>
              </div>
            )}
          </CardContent>
        </Card>
          )}
        </>
      )}
      
      {/* ===== PLANS TAB ===== */}
      {activeSubTab === 'plans' && (
        <>
          {/* Plans Header */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold">Pasif Alan İş Planları</h3>
                  <p className="text-sm text-gray-500">Pasif alanlar için planlanan faaliyetler</p>
                </div>
                <div className="flex items-center gap-2">
                  {/* Region Filter for Plans - Geniş kapsamlı kullanıcılar için */}
                  {canSeeAllScope && visibleRegions.length > 0 && (
                    <SearchableSelect
                      value={planFilterRegion}
                      onChange={setPlanFilterRegion}
                      allLabel="Tüm Bölgeler"
                      searchPlaceholder="Bölge ara..."
                      className="w-[160px]"
                      options={visibleRegions.map((region) => ({ id: region.id, name: region.name }))}
                    />
                  )}

                  {/* Formen Filter for Plans - Geniş kapsamlı kullanıcılar için */}
                  {canSeeAllScope && visibleFormens.length > 0 && (
                    <SearchableSelect
                      value={planFilterFormenId}
                      onChange={setPlanFilterFormenId}
                      allLabel="Tüm Formenler"
                      searchPlaceholder="Formen ara..."
                      className="w-[160px]"
                      options={visibleFormens.map((formen) => ({ id: formen.id, name: formen.name }))}
                    />
                  )}
                  
                  {canCreatePlan && (
                    <Button onClick={openNewPlanDialog} className="bg-amber-600 hover:bg-amber-700">
                      <Plus className="h-4 w-4 mr-2" />
                      Yeni Plan Oluştur
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Plans List */}
          {loadingPlans ? (
            <div className="text-center py-8 text-gray-500">Yükleniyor...</div>
          ) : plans.length === 0 ? (
            <Card className="p-12 text-center">
              <CalendarPlus className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-600">Henüz plan yok</h3>
              <p className="text-gray-500 mt-2">Pasif alanlar için iş planı oluşturun.</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Group plans by date with filters */}
              {(() => {
                // Planları filtrele
                const filteredPlans = plans.filter(plan => {
                  const area = passiveAreas.find(a => a.id === plan.passive_area_id);
                  const matchesRegion = planFilterRegion === 'all' || area?.region_id === planFilterRegion;
                  const matchesFormen = planFilterFormenId === 'all' || plan.created_by_id === planFilterFormenId;
                  
                  // Role-based visibility for plans
                  if (isMudurYardimcisi) {
                    const isInMyRegion = area ? myRegionIds.includes(area.region_id) : false;
                    const isAssignedToMe = area ? area.assigned_mudur_yardimcisi_id === personnelId : false;
                    return matchesRegion && matchesFormen && (isInMyRegion || isAssignedToMe || !area);
                  }
                  if (isBolgeMuhendisi) {
                    const isInMyRegion = area ? myRegionIds.includes(area.region_id) : false;
                    const isAssignedToMe = area ? area.assigned_bolge_muhendisi_id === personnelId : false;
                    return matchesRegion && matchesFormen && (isInMyRegion || isAssignedToMe || !area);
                  }
                  if (isFormen) {
                    return area ? area.assigned_formen_id === personnelId : plan.created_by_id === personnelId;
                  }
                  
                  return matchesRegion && matchesFormen;
                });
                
                if (filteredPlans.length === 0) {
                  return (
                    <Card className="p-12 text-center">
                      <CalendarPlus className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                      <h3 className="text-lg font-semibold text-gray-600">Filtreye uygun plan yok</h3>
                    </Card>
                  );
                }
                
                return Object.entries(
                  filteredPlans.reduce((acc, plan) => {
                    const date = plan.planned_date;
                    if (!acc[date]) acc[date] = [];
                    acc[date].push(plan);
                    return acc;
                  }, {})
                ).sort(([a], [b]) => a.localeCompare(b)).map(([date, datePlans]) => (
                <Card key={date}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-amber-600" />
                      <CardTitle className="text-lg">
                        {new Date(date).toLocaleDateString('tr-TR', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </CardTitle>
                      <Badge variant="outline">{datePlans.length} plan</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {datePlans.map(plan => {
                        const statusInfo = PLAN_STATUS_COLORS[plan.status] || PLAN_STATUS_COLORS.planned;
                        const activityLabel = PLAN_ACTIVITY_TYPES.find(t => t.value === plan.activity_type)?.label || plan.activity_type;
                        
                        return (
                          <div 
                            key={plan.id} 
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium">{plan.passive_area_name}</span>
                                {plan.neighborhood && (
                                  <span className="text-sm text-gray-500">- {plan.neighborhood}</span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <Badge variant="secondary">{activityLabel}</Badge>
                                <Badge className={`${statusInfo.bg} ${statusInfo.text}`}>
                                  {statusInfo.label}
                                </Badge>
                              </div>
                              {plan.description && (
                                <p className="text-sm text-gray-600 mt-1">{plan.description}</p>
                              )}
                              {plan.completion_note && (
                                <p className="text-sm text-gray-500 mt-1 italic">
                                  {plan.status === 'incomplete' ? '❌ ' : '✅ '}{plan.completion_note}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {plan.status === 'planned' && (
                                <>
                                  {/* Tamamlandı / Tamamlanmadı — pasif_alan.duzenle gerektirir */}
                                  {canEdit && (
                                    <>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="text-green-600 hover:bg-green-50 border-green-200"
                                        onClick={() => handleQuickComplete(plan, 'completed')}
                                      >
                                        <CheckCircle2 className="h-4 w-4 mr-1" />
                                        Tamamlandı
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="text-red-600 hover:bg-red-50 border-red-200"
                                        onClick={() => {
                                          setPlanToComplete(plan);
                                          setCompletionNote('');
                                          setIncompleteDialogOpen(true);
                                        }}
                                      >
                                        <XCircle className="h-4 w-4 mr-1" />
                                        Tamamlanmadı
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleEditPlan(plan)}
                                      >
                                        <Edit className="h-4 w-4" />
                                      </Button>
                                    </>
                                  )}
                                  {canDelete && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-red-600"
                                      onClick={() => {
                                        setPlanToDelete(plan);
                                        setDeletePlanDialogOpen(true);
                                      }}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              ));
              })()}
            </div>
          )}
        </>
      )}
      
      {/* ===== HISTORY TAB ===== */}
      {activeSubTab === 'history' && (
        <>
          {/* History Header */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold">Geçmiş / Envanter</h3>
                  <p className="text-sm text-gray-500">Pasif alanlarda yapılan tüm faaliyetlerin kaydı</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {/* Region Filter for History - Geniş kapsamlı kullanıcılar için */}
                  {canSeeAllScope && visibleRegions.length > 0 && (
                    <SearchableSelect
                      value={historyFilterRegion}
                      onChange={setHistoryFilterRegion}
                      allLabel="Tüm Bölgeler"
                      searchPlaceholder="Bölge ara..."
                      className="w-[150px]"
                      options={visibleRegions.map((region) => ({ id: region.id, name: region.name }))}
                    />
                  )}

                  {/* Formen Filter for History - Geniş kapsamlı kullanıcılar için */}
                  {canSeeAllScope && visibleFormens.length > 0 && (
                    <SearchableSelect
                      value={historyFilterFormenId}
                      onChange={setHistoryFilterFormenId}
                      allLabel="Tüm Formenler"
                      searchPlaceholder="Formen ara..."
                      className="w-[150px]"
                      options={visibleFormens.map((formen) => ({ id: formen.id, name: formen.name }))}
                    />
                  )}

                  {/* Faaliyet Türü Filtresi - dinamik (getirilen kayıtlardan), tüm roller için */}
                  {visibleActivityTypes.length > 0 && (
                    <SearchableSelect
                      value={historyFilterActivityType}
                      onChange={setHistoryFilterActivityType}
                      allLabel="Tüm Faaliyetler"
                      searchPlaceholder="Faaliyet ara..."
                      className="w-[170px]"
                      options={visibleActivityTypes.map((type) => ({ id: type.value, name: type.label }))}
                    />
                  )}

                  <div className="w-72">
                    <Label className="text-xs text-gray-500">Tarih Aralığı</Label>
                    <DateRangePicker
                      startDate={historyDateRange.start}
                      endDate={historyDateRange.end}
                      onChange={({ start, end }) => setHistoryDateRange({ start, end })}
                      placeholder="Tarih aralığı"
                      manualInput
                    />
                  </div>
                  <Button 
                    variant="outline" 
                    onClick={() => fetchAllActivities(true)}
                    className="mt-5"
                  >
                    <Search className="h-4 w-4 mr-1" />
                    Ara
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* History List */}
          {loadingAllActivities ? (
            <div className="text-center py-8 text-gray-500">Yükleniyor...</div>
          ) : allActivities.length === 0 ? (
            <Card className="p-12 text-center">
              <History className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-600">Kayıt bulunamadı</h3>
              <p className="text-gray-500 mt-2">Seçili tarih aralığında faaliyet kaydı yok.</p>
            </Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tarih</TableHead>
                    <TableHead>Pasif Alan</TableHead>
                    <TableHead>Faaliyet Türü</TableHead>
                    <TableHead>Durum</TableHead>
                    <TableHead>Not</TableHead>
                    <TableHead>Yapan</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allActivities
                    .filter(activity => {
                      // Role-based filtering for history
                      const area = passiveAreas.find(a => a.id === activity.passive_area_id);
                      
                      // Region filter
                      const matchesRegion = historyFilterRegion === 'all' || area?.region_id === historyFilterRegion;
                      
                      // Formen filter
                      const matchesFormen = historyFilterFormenId === 'all' || activity.personnel_id === historyFilterFormenId;

                      // Faaliyet türü filtresi (dinamik) - tüm roller için geçerli
                      const matchesActivityType = historyFilterActivityType === 'all' || activity.activity_type === historyFilterActivityType;

                      // Role-based visibility
                      if (isMudurYardimcisi) {
                        // Müdür Yardımcısı: Kendisine bağlı bölgelerdeki pasif alanların geçmişini görebilir
                        const isInMyRegion = area ? myRegionIds.includes(area.region_id) : false;
                        const isAssignedToMe = area ? area.assigned_mudur_yardimcisi_id === personnelId : false;
                        return matchesActivityType && matchesRegion && matchesFormen && (isInMyRegion || isAssignedToMe || !area);
                      }
                      if (isBolgeMuhendisi) {
                        // Bölge Mühendisi: Kendisine bağlı bölgelerdeki pasif alanların geçmişini görebilir
                        const isInMyRegion = area ? myRegionIds.includes(area.region_id) : false;
                        const isAssignedToMe = area ? area.assigned_bolge_muhendisi_id === personnelId : false;
                        return matchesActivityType && matchesRegion && matchesFormen && (isInMyRegion || isAssignedToMe || !area);
                      }
                      if (isFormen) {
                        return matchesActivityType && (area ? area.assigned_formen_id === personnelId : true);
                      }
                      return matchesActivityType && matchesRegion && matchesFormen; // Admin sees all with filters
                    })
                    .map(activity => {
                      const activityLabel = resolveActivityLabel(activity.activity_type);
                      // Durumu nottan çıkar
                      const isIncomplete = activity.notes?.startsWith('[Tamamlanmadı]') || activity.status === 'incomplete';
                      const isCompleted = activity.notes?.startsWith('[Tamamlandı]') || activity.status === 'completed';
                      // Notu temizle
                      const cleanNotes = activity.notes?.replace(/^\[(Tamamlandı|Tamamlanmadı)\]\s*/, '') || '';
                      
                      return (
                        <TableRow key={activity.id}>
                          <TableCell className="font-medium">
                            {new Date(activity.activity_date).toLocaleDateString('tr-TR')}
                          </TableCell>
                          <TableCell>{activity.passive_area_name}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{activityLabel}</Badge>
                          </TableCell>
                          <TableCell>
                            {isIncomplete ? (
                              <Badge className="bg-red-100 text-red-700">Tamamlanmadı</Badge>
                            ) : isCompleted ? (
                              <Badge className="bg-green-100 text-green-700">Tamamlandı</Badge>
                            ) : (
                              <Badge variant="secondary">-</Badge>
                            )}
                          </TableCell>
                          <TableCell className="max-w-xs truncate" title={cleanNotes}>
                            {cleanNotes || activity.description || '-'}
                          </TableCell>
                          <TableCell>{activity.personnel_name || '-'}</TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </Card>
          )}
        </>
      )}
      
      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent 
          className="max-w-4xl max-h-[90vh] overflow-y-auto"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader className="flex flex-row items-start justify-between">
            <div>
              <DialogTitle>
                {isFormReadOnly 
                  ? '📋 Pasif Alan Detayları' 
                  : (isEditing ? 'Pasif Alan Düzenle' : 'Yeni Pasif Alan')
                }
              </DialogTitle>
              <DialogDescription>
                {isFormReadOnly 
                  ? 'Bu alanı görüntülüyorsunuz' 
                  : 'Pasif alan bilgilerini girin'
                }
              </DialogDescription>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 rounded-full hover:bg-red-100 hover:text-red-600 -mt-2 -mr-2"
              onClick={() => setIsDialogOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </DialogHeader>
          
          {/* Görüntüleme modu uyarısı */}
          {isFormReadOnly && (
            <Alert className="bg-amber-50 border-amber-200">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                ⚠️ Bu formu sadece görüntüleme modunda açtınız. Değişiklik yapamazsınız.
              </AlertDescription>
            </Alert>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column - Form Fields */}
            <div className="space-y-4">
              <div>
                <Label>Pasif Alan Adı *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Örn: Pasif Alan 1 - Çiğdem Mahallesi"
                  disabled={isFormReadOnly}
                />
              </div>
              
              <div>
                <Label>Mahalle</Label>
                <Input
                  value={form.neighborhood}
                  onChange={(e) => setForm(prev => ({ ...prev, neighborhood: e.target.value }))}
                  placeholder="Mahalle adı"
                  disabled={isFormReadOnly}
                />
              </div>
              
              <div>
                <Label>Açık Adres</Label>
                <Textarea
                  value={form.address}
                  onChange={(e) => setForm(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Detaylı adres bilgisi"
                  rows={2}
                  disabled={isFormReadOnly}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Enlem</Label>
                  <Input
                    type="number"
                    step="any"
                    value={form.latitude || ''}
                    onChange={(e) => setForm(prev => ({ ...prev, latitude: parseFloat(e.target.value) || null }))}
                    placeholder="39.925533"
                    disabled={isFormReadOnly}
                  />
                </div>
                <div>
                  <Label>Boylam</Label>
                  <Input
                    type="number"
                    step="any"
                    value={form.longitude || ''}
                    onChange={(e) => setForm(prev => ({ ...prev, longitude: parseFloat(e.target.value) || null }))}
                    placeholder="32.866287"
                    disabled={isFormReadOnly}
                  />
                </div>
              </div>
              
              <div>
                <Label>Alan Türü</Label>
                <SearchableSelect
                  value={form.area_type}
                  onChange={(v) => setForm(prev => ({ ...prev, area_type: v }))}
                  disabled={isFormReadOnly}
                  showAllOption={false}
                  placeholder="Tür seçin"
                  searchPlaceholder="Alan türü ara..."
                  options={AREA_TYPES.map((type) => ({ id: type.value, name: type.label }))}
                />
              </div>
              
              <div>
                <Label>Toplam Alan (m²)</Label>
                <Input
                  type="number"
                  value={form.area || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, area: parseFloat(e.target.value) || null }))}
                  placeholder="Otomatik hesaplanır veya manuel girin"
                  disabled={isFormReadOnly}
                />
              </div>
              
              <div>
                <Label>Bölge</Label>
                <SearchableSelect
                  value={form.region_id || "none"}
                  onChange={(v) => setForm(prev => ({ ...prev, region_id: v === "none" ? "" : v }))}
                  disabled={isFormReadOnly}
                  allValue="none"
                  allLabel="Bölge Yok"
                  searchPlaceholder="Bölge ara..."
                  options={regions.map((region) => ({ id: region.id, name: region.name }))}
                />
              </div>
              
              {/* Personnel Assignments */}
              <div>
                <Label>Formen</Label>
                <SearchableSelect
                  value={form.assigned_formen_id || "none"}
                  onChange={(v) => setForm(prev => ({ ...prev, assigned_formen_id: v === "none" ? "" : v }))}
                  disabled={isFormReadOnly}
                  allValue="none"
                  allLabel="Atanmamış"
                  searchPlaceholder="Formen ara..."
                  options={formens.map((p) => ({ id: p.id, name: p.name }))}
                />
              </div>
              
              <div>
                <Label>Bölge Mühendisi</Label>
                <SearchableSelect
                  value={form.assigned_bolge_muhendisi_id || "none"}
                  onChange={(v) => setForm(prev => ({ ...prev, assigned_bolge_muhendisi_id: v === "none" ? "" : v }))}
                  disabled={isFormReadOnly}
                  allValue="none"
                  allLabel="Atanmamış"
                  searchPlaceholder="Bölge mühendisi ara..."
                  options={bolgeMuhendisleri.map((p) => ({ id: p.id, name: p.name }))}
                />
              </div>
              
              <div>
                <Label>Müdür Yardımcısı</Label>
                <SearchableSelect
                  value={form.assigned_mudur_yardimcisi_id || "none"}
                  onChange={(v) => setForm(prev => ({ ...prev, assigned_mudur_yardimcisi_id: v === "none" ? "" : v }))}
                  disabled={isFormReadOnly}
                  allValue="none"
                  allLabel="Atanmamış"
                  searchPlaceholder="Müdür yardımcısı ara..."
                  options={mudurYardimcilari.map((p) => ({ id: p.id, name: p.name }))}
                />
              </div>
              
              <div>
                <Label>Notlar</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Ek notlar..."
                  rows={3}
                  disabled={isFormReadOnly}
                />
              </div>
            </div>
            
            {/* Right Column - Map */}
            <div className="space-y-4">
              <div>
                <Label>Harita</Label>
                {!isFormReadOnly && (
                  <div className="flex gap-2 mb-2">
                    <Button
                      type="button"
                      variant={drawingMode ? "default" : "outline"}
                      size="sm"
                      onClick={() => setDrawingMode(!drawingMode)}
                    >
                      {drawingMode ? "Çizimi Durdur" : "Sınır Çiz"}
                    </Button>
                    {drawnPoints.length > 0 && (
                      <>
                        <Button type="button" variant="outline" size="sm" onClick={handleSaveBoundaries}>
                          Sınırı Kaydet
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={handleClearBoundaries}>
                          Sınırı Temizle
                        </Button>
                      </>
                    )}
                  </div>
                )}
                <p className="text-xs text-gray-500 mb-2">
                  {isFormReadOnly 
                    ? "Bu alan salt okunur modda görüntüleniyor" 
                    : (drawingMode 
                        ? "Haritaya tıklayarak sınır noktalarını ekleyin" 
                        : "Konum seçmek için haritaya tıklayın")
                  }
                </p>
              </div>
              
              <div className="h-[400px] rounded-lg overflow-hidden border relative">
                <MapLayerToggle value={formMapLayer} onChange={setFormMapLayer} floating />
                {!isFormReadOnly && (
                  <MapSearchBox apiBase={apiUrl} onResult={(lat, lng) => setFormSearchCenter([lat, lng])} />
                )}
                <MapContainer
                  center={mapCenter}
                  zoom={15}
                  style={{ height: '100%', width: '100%' }}
                >
                  <MapLayerTile layer={formMapLayer} />
                  <RecenterMap center={formSearchCenter} />

                  {/* Location Marker */}
                  {form.latitude && form.longitude && (
                    <Marker position={[form.latitude, form.longitude]} icon={pinIcon(NEARBY_SOURCES.pasif.color)} />
                  )}
                  
                  {/* Drawing Mode */}
                  {drawingMode ? (
                    <SimpleBoundaryMarker onPointAdd={handleBoundaryPointAdd} />
                  ) : (
                    <SimpleLocationMarker onLocationSelect={handleLocationSelect} />
                  )}
                  
                  {/* Drawn Polygon */}
                  {drawnPoints.length >= 3 && (
                    <Polygon 
                      positions={drawnPoints} 
                      pathOptions={{ color: '#d97706', fillColor: '#fbbf24', fillOpacity: 0.3 }}
                    />
                  )}
                  
                  {/* Çizilen sınır köşe noktaları — mavi ikon yerine küçük amber daireler
                      (kafa karışmasın; mavi ikon sadece merkez konum için). */}
                  {drawnPoints.map((point, idx) => (
                    <CircleMarker
                      key={idx}
                      center={point}
                      radius={5}
                      pathOptions={{ color: '#b45309', fillColor: '#f59e0b', fillOpacity: 1, weight: 2 }}
                    >
                      <Tooltip direction="top" offset={[0, -6]}>{idx + 1}. nokta</Tooltip>
                    </CircleMarker>
                  ))}
                </MapContainer>
              </div>
              
              {drawnPoints.length > 0 && (
                <p className="text-sm text-gray-600">
                  {drawnPoints.length} nokta çizildi
                  {form.area && ` • Tahmini alan: ${form.area.toLocaleString()} m²`}
                </p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              {isFormReadOnly ? 'Kapat' : 'İptal'}
            </Button>
            {!isFormReadOnly && (
              <Button onClick={handleSubmit} className="bg-amber-600 hover:bg-amber-700">
                {isEditing ? 'Güncelle' : 'Kaydet'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pasif Alanı Sil</DialogTitle>
            <DialogDescription>
              &quot;{areaToDelete?.name}&quot; pasif alanını silmek istediğinizden emin misiniz?
              Bu işlem geri alınamaz ve tüm faaliyetler de silinecektir.
            </DialogDescription>
          </DialogHeader>

          <DeleteNameConfirm
            name={areaToDelete?.name}
            value={areaDeleteConfirmText}
            onChange={setAreaDeleteConfirmText}
            label="alanın adını"
          />

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>İptal</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={!areaDeleteConfirmed}>
              Sil
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Activity Dialog */}
      <Dialog open={activityDialogOpen} onOpenChange={setActivityDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Faaliyetler - {selectedAreaForActivity?.name}</DialogTitle>
            <DialogDescription>Bu pasif alanda yapılan çalışmalar</DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="list" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="list">Faaliyet Listesi</TabsTrigger>
              {canCreate && <TabsTrigger value="add">Yeni Faaliyet Ekle</TabsTrigger>}
            </TabsList>
            
            <TabsContent value="list">
              {loadingActivities ? (
                <div className="text-center py-8 text-gray-500">Yükleniyor...</div>
              ) : activities.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Henüz faaliyet kaydı yok
                </div>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {activities.map(activity => (
                    <Card key={activity.id}>
                      <CardContent className="pt-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Badge>{ACTIVITY_TYPES.find(t => t.value === activity.activity_type)?.label || activity.activity_type}</Badge>
                              <span className="text-sm text-gray-500">{activity.activity_date}</span>
                            </div>
                            <p className="font-medium">{activity.description}</p>
                            {activity.personnel_name && (
                              <p className="text-sm text-gray-600 mt-1">
                                <User className="h-3 w-3 inline mr-1" />
                                {activity.personnel_name}
                              </p>
                            )}
                            {activity.notes && (
                              <p className="text-sm text-gray-500 mt-1">{activity.notes}</p>
                            )}
                          </div>
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteActivity(activity.id)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="add">
              <div className="space-y-4">
                <div>
                  <Label>Faaliyet Türü</Label>
                  <SearchableSelect
                    value={activityForm.activity_type}
                    onChange={(v) => setActivityForm(prev => ({ ...prev, activity_type: v }))}
                    showAllOption={false}
                    placeholder="Tür seçin"
                    searchPlaceholder="Faaliyet türü ara..."
                    options={ACTIVITY_TYPES.map((type) => ({ id: type.value, name: type.label }))}
                  />
                </div>
                
                <div>
                  <Label>Açıklama *</Label>
                  <Textarea
                    value={activityForm.description}
                    onChange={(e) => setActivityForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Yapılan işin detaylı açıklaması..."
                    rows={3}
                  />
                </div>
                
                <div>
                  <DatePicker
                    label="Tarih"
                    value={activityForm.activity_date}
                    onChange={(value) => setActivityForm(prev => ({ ...prev, activity_date: value }))}
                  />
                </div>
                
                <div>
                  <Label>Yapan Personel</Label>
                  <SearchableSelect
                    value={activityForm.personnel_id || "none"}
                    onChange={(v) => setActivityForm(prev => ({ ...prev, personnel_id: v === "none" ? "" : v }))}
                    allValue="none"
                    allLabel="Belirtilmemiş"
                    searchPlaceholder="Personel ara..."
                    options={personnel.map((p) => ({ id: p.id, name: p.name }))}
                  />
                </div>
                
                <div>
                  <Label>Ek Notlar</Label>
                  <Textarea
                    value={activityForm.notes}
                    onChange={(e) => setActivityForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Varsa ek notlar..."
                    rows={2}
                  />
                </div>
                
                <Button onClick={handleSubmitActivity} className="w-full bg-amber-600 hover:bg-amber-700">
                  Faaliyet Kaydet
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
      
      {/* Plan Create/Edit Dialog */}
      <Dialog open={planDialogOpen} onOpenChange={setPlanDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingPlan ? 'Plan Düzenle' : 'Yeni Plan Oluştur'}</DialogTitle>
            <DialogDescription>Pasif alan için iş planı oluşturun</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Pasif Alan *</Label>
              <SearchableSelect
                value={planForm.passive_area_id || "none"}
                onChange={(v) => setPlanForm(prev => ({ ...prev, passive_area_id: v === "none" ? "" : v }))}
                allValue="none"
                allLabel="Seçiniz"
                searchPlaceholder="Pasif alan ara..."
                options={filteredAreas.map((area) => ({
                  id: area.id,
                  name: `${area.name}${area.neighborhood ? ` - ${area.neighborhood}` : ''}`,
                }))}
              />
            </div>
            
            <div>
              <Label>Faaliyet Türü *</Label>
              <SearchableSelect
                value={planForm.activity_type}
                onChange={(v) => setPlanForm(prev => ({ ...prev, activity_type: v }))}
                showAllOption={false}
                placeholder="Tür seçin"
                searchPlaceholder="Faaliyet türü ara..."
                options={PLAN_ACTIVITY_TYPES.map((type) => ({ id: type.value, name: type.label }))}
              />
            </div>
            
            <div>
              <DatePicker
                label="Planlanan Tarih"
                required
                value={planForm.planned_date}
                onChange={(value) => setPlanForm(prev => ({ ...prev, planned_date: value }))}
                highlightHolidays
              />
            </div>
            
            <div>
              <Label>Açıklama (Opsiyonel)</Label>
              <Textarea
                value={planForm.description}
                onChange={(e) => setPlanForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Plan hakkında ek bilgi..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanDialogOpen(false)}>İptal</Button>
            <Button onClick={handleCreatePlan} className="bg-amber-600 hover:bg-amber-700">
              {editingPlan ? 'Güncelle' : 'Oluştur'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Plan Delete Confirmation Dialog */}
      <Dialog open={deletePlanDialogOpen} onOpenChange={setDeletePlanDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Planı Sil</DialogTitle>
            <DialogDescription>
              &quot;{planToDelete?.passive_area_name}&quot; için oluşturulan planı silmek istediğinizden emin misiniz?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletePlanDialogOpen(false)}>İptal</Button>
            <Button variant="destructive" onClick={handleDeletePlan}>Sil</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Tamamlanmadı - Açıklama Dialog */}
      <Dialog open={incompleteDialogOpen} onOpenChange={setIncompleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="h-5 w-5" />
              Plan Tamamlanmadı
            </DialogTitle>
            <DialogDescription>
              {planToComplete?.passive_area_name} - {new Date(planToComplete?.planned_date).toLocaleDateString('tr-TR')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Neden tamamlanmadı? *</Label>
              <Textarea
                value={completionNote}
                onChange={(e) => setCompletionNote(e.target.value)}
                placeholder="Tamamlanamama nedenini yazın... (Örn: Hava koşulları, ekipman arızası, personel eksikliği)"
                rows={4}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIncompleteDialogOpen(false)}>İptal</Button>
            <Button 
              onClick={handleIncompletePlan} 
              className="bg-red-600 hover:bg-red-700"
              disabled={!completionNote.trim()}
            >
              Tamamlanmadı Olarak İşaretle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TabsContent>
  );
}
